"use client";

import React, { useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import {
  Home,
  ShoppingCart,
  ShoppingBag,
  User,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Badge, IconButton } from "@mui/material";
import { useAppSelector } from "@/hooks/useAppSelector";
import { getTotalCartQuantity } from "@/utils/helpers";
import ProfileDrawer from "./ProfileDrawer";
import MobileBuildYourCart from "./MobileBuildYourCart";
import HeaderWhatsApp from "@/middleware/HeaderWhatsapp";

const MobileNavigation: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cartExpanded, setCartExpanded] = useState(false);
  const { cart } = useAppSelector((state) => state.cart);
  const router = useRouter();
  const pathname = usePathname();
  const cartItemCount = getTotalCartQuantity(cart);

  const isActive = (path: string) => pathname === path;
  const isOrderActive = pathname.startsWith("/user/orders");
  const isProfileActive = pathname.startsWith("/user") && !isOrderActive;

  return (
    <>
      {cartExpanded && (
        <div
          className="fixed inset-0 z-30 bg-black/30 backdrop-blur-sm"
          onClick={() => setCartExpanded(false)}
        />
      )}

      {/* ✅ Show MobileBuildYourCart only on homepage */}
      {pathname === "/" && (
        <div className="fixed bottom-[50px] w-[95%] z-40 left-1/2 transform -translate-x-1/2">
          <MobileBuildYourCart expanded={cartExpanded} />
        </div>
      )}

      {/* Bottom Navigation */}
      <nav className="h-[60px] fixed bottom-[16px] z-50 left-1/2 transform -translate-x-1/2 w-[95%] bg-white-1 rounded-full shadow-lg border border-gray-300 py-2 px-5 flex items-center justify-between max-w-lg">
        {/* Home */}
        <IconButton onClick={() => router.push("/")}>
          <Home
            size={28}
            className={`${isActive("/") ? "text-orange-500" : "text-gray-700"}`}
          />
        </IconButton>

        {/* Cart */}
        <IconButton onClick={() => router.push("/checkout/cart")}>
          <Badge
            badgeContent={cartItemCount}
            overlap="circular"
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{
              "& .MuiBadge-badge": {
                transform: "translate(20px, -15px)",
                background: "var(--color-green)",
                color: "white",
              },
            }}
          >
            <ShoppingCart
              size={28}
              className={`${
                isActive("/checkout/cart") ? "text-orange-500" : "text-gray-700"
              }`}
            />
          </Badge>
        </IconButton>

        {/* Floating Expand/Collapse Button */}
        {/* Center Area: Chevron Toggle (Home) or WhatsApp Icon */}
        <div className="relative flex justify-center items-center w-[80px]">
          <IconButton
            sx={{
              position: "absolute",
              top: "-56px",
              width: "65px",
              height: "65px",
              borderRadius: "50%",
              border: "5px solid #f3f3f3",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "var(--color-green)",
              color: "white",
              "&:hover": {
                background: "var(--color-green-hover)",
              },
            }}
            onClick={() => pathname === "/" && setCartExpanded((prev) => !prev)}
          >
            {pathname === "/" ? (
              cartExpanded ? (
                <ChevronDown size={32} color="white" />
              ) : (
                <ChevronUp size={32} color="white" />
              )
            ) : (
              <HeaderWhatsApp />
            )}
          </IconButton>
        </div>

        {/* Orders */}
        <IconButton onClick={() => router.push("/user/orders")}>
          <ShoppingBag
            size={28}
            className={`${isOrderActive ? "text-orange-500" : "text-gray-700"}`}
          />
        </IconButton>

        {/* Profile */}
        <IconButton onClick={() => setDrawerOpen(true)}>
          <User
            size={28}
            className={`${
              isProfileActive ? "text-orange-500" : "text-gray-700"
            }`}
          />
        </IconButton>
      </nav>

      {/* Profile Drawer */}
      <ProfileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpen={() => setDrawerOpen(true)}
      />
    </>
  );
};

export default MobileNavigation;
